import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, Plus, Minus, X, ArrowLeft, ShoppingCart } from "lucide-react";
import { getAllCatagoryServices } from "../../services/services-module-api";
import { useServiceContext } from "../../context/service-context";
import { Clock, Zap } from "lucide-react";
import CartSidebar from "./cart-sidebar";

const SubCategories = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setSelectedService, setQuantity } = useServiceContext();

  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryName, setCategoryName] = useState("");

  const [cartItems, setCartItems] = useState([]);
  const [editingPrices, setEditingPrices] = useState({});
  const [selectedServiceDetails, setSelectedServiceDetails] = useState(null);
  const [showServiceTypeModal, setShowServiceTypeModal] = useState(false);

  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => window.scrollTo(0, 0), []);

  useEffect(() => {
    const storedCart = localStorage.getItem("cartItems");
    if (storedCart) setCartItems(JSON.parse(storedCart));
  }, []);

  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    if (cartItems.length === 0) localStorage.removeItem("serviceType");
  }, [cartItems]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await getAllCatagoryServices(id);

        if (response?.success && Array.isArray(response?.data?.data)) {
          const fetched = response.data.data;
          const normalized = fetched.map((s) => ({ ...s, id: s.id || s._id }));
          setSubCategories(normalized);

          if (normalized[0]?.mainService?.name)
            setCategoryName(normalized[0].mainService.name);
        } else {
          setSubCategories([]);
        }
      } catch (err) {
        setSubCategories([]);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchServices();
  }, [id]);

  const persistSelectedCategory = (nextCartItems) => {
    const categoryData = { categoryName, cartItems: nextCartItems };
    localStorage.setItem("selectedCategory", JSON.stringify(categoryData));
  };

  const handleQuantityChange = (serviceId, delta) => {
    setCartItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === serviceId) {
          const newQty = Math.max(1, (item.quantity ?? 1) + delta);
          const base = item.basePrice || 0;
          return { ...item, quantity: newQty, customPrice: base * newQty };
        }
        return item;
      }),
    );
  };

  const handleAddToCart = (service) => {
    const normalized = { ...service, id: service?.id || service?._id };
    const existingItem = cartItems.find((item) => item.id === normalized.id);

    if (existingItem) {
      handleQuantityChange(normalized.id, 1);
    } else {
      const base = normalized.basePrice || 0;
      setCartItems([
        ...cartItems,
        { ...normalized, quantity: 1, customPrice: base },
      ]);
    }

    setSelectedService(normalized);
    setQuantity(1);
  };

  const handleRemoveFromCart = (serviceId) => {
    setCartItems((prev) => prev.filter((item) => item.id !== serviceId));
    setEditingPrices((prev) => {
      const next = { ...prev };
      delete next[serviceId];
      return next;
    });
  };

  const handleClearAllCart = () => {
    setCartItems([]);
    setEditingPrices({});
    localStorage.removeItem("cartItems");
    localStorage.removeItem("selectedCategory");
    localStorage.removeItem("serviceType");
    setIsCartOpen(false);
  };

  const handlePriceEdit = (serviceId, value, minValue = 0) => {
    const numericValue = Number.parseFloat(value);
    const safeValue = Number.isFinite(numericValue)
      ? Math.max(minValue, numericValue)
      : minValue;

    setEditingPrices((prev) => ({ ...prev, [serviceId]: safeValue }));
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === serviceId ? { ...item, customPrice: safeValue } : item,
      ),
    );
  };

  const handleContinue = () => {
    if (!cartItems?.length) return;
    persistSelectedCategory(cartItems);

    const existingType = localStorage.getItem("serviceType");
    if (existingType) return navigate("/customer/service-request");

    setShowServiceTypeModal(true);
  };

  const handleServiceSelect = (type) => {
    if (!cartItems?.length) return;
    persistSelectedCategory(cartItems);

    localStorage.setItem("serviceType", type);
    setShowServiceTypeModal(false);
    navigate("/customer/service-request");
  };

  const handleViewMore = (service) => setSelectedServiceDetails(service);
  const closeDetailsModal = () => setSelectedServiceDetails(null);
  const handleBack = () => navigate("/services");

  const filteredServices = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return subCategories.filter((service) =>
      service?.name?.toLowerCase().includes(q),
    );
  }, [subCategories, searchTerm]);

  const cartTotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.customPrice || 0), 0),
    [cartItems],
  );

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#005b9f]" />
      </div>
    );

  return (
    <div className="bg-gray-50 min-h-screen pb-24">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-[#005b9f] hover:text-[#004a80] font-semibold transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Services
          </button>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative w-full md:w-[420px]">
              <input
                type="text"
                placeholder="Search services..."
                className="w-full border-2 border-gray-300 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005b9f] focus:border-transparent transition"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>

            <button
              type="button"
              onClick={() => setIsCartOpen(true)}
              className="relative shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-gray-200 shadow-sm hover:shadow transition"
              aria-label="Open cart"
            >
              <ShoppingCart className="h-5 w-5 text-[#005b9f]" />
              <span className="text-sm font-bold text-slate-800">
                Your Cart
              </span>
              {cartItems.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#005b9f] text-white text-xs font-bold h-6 min-w-[24px] px-2 rounded-full flex items-center justify-center">
                  {cartItems.length}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {categoryName || "Services"}
          </h1>
          <p className="text-gray-600">
            Add services, then open cart to manage them.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredServices.map((service) => {
            const serviceId = service?.id || service?._id;
            const cartItem = cartItems.find((i) => i.id === serviceId);
            const isInCart = !!cartItem;

            const qty = cartItem?.quantity ?? 1;
            const base = cartItem?.basePrice ?? service?.basePrice ?? 0;
            const minPrice = base * qty;

            const currentPrice =
              cartItem?.customPrice ??
              editingPrices[serviceId] ??
              service?.basePrice ??
              0;

            return (
              <div
                key={serviceId}
                className="bg-white border border-gray-200 rounded-xl shadow hover:shadow-md transition-all duration-200 overflow-hidden"
              >
                <div className="flex items-center p-3 gap-3">
                  {service?.image && (
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                    />
                  )}

                  <div className="flex flex-col flex-1">
                    <h3 className="font-semibold text-sm text-[#005b9f] truncate">
                      {service?.name}
                    </h3>

                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="number"
                        value={currentPrice}
                        onChange={(e) =>
                          handlePriceEdit(serviceId, e.target.value, minPrice)
                        }
                        onKeyDown={(e) => {
                          if (
                            e.key === "ArrowDown" ||
                            e.key === "-" ||
                            e.key === "Subtract"
                          )
                            e.preventDefault();
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-24 border border-gray-300 rounded-md px-2 py-1 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#005b9f]"
                        min={minPrice}
                      />
                      <span className="text-xs font-medium text-gray-600">
                        Rs.
                      </span>
                    </div>

                    {isInCart ? (
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => handleQuantityChange(serviceId, -1)}
                          className="bg-red-500 text-white p-1 rounded hover:bg-red-600 transition"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="h-3 w-3" />
                        </button>

                        <span className="text-xs font-semibold text-gray-700">
                          {cartItem.quantity}
                        </span>

                        <button
                          onClick={() => handleQuantityChange(serviceId, 1)}
                          className="bg-green-500 text-white p-1 rounded hover:bg-green-600 transition"
                          aria-label="Increase quantity"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleAddToCart(service)}
                        className="mt-2 bg-[#005b9f] text-white text-xs px-3 py-1.5 rounded hover:bg-[#004a80] transition flex items-center justify-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add to Booking
                      </button>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-100">
                  <button
                    onClick={() => handleViewMore(service)}
                    className="w-full flex items-center justify-center gap-1 py-2 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    View More
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {filteredServices.length === 0 && (
          <div className="text-center py-16">
            <p className="text-lg text-gray-600 font-semibold">
              {searchTerm
                ? "No services found matching your search."
                : "No services available."}
            </p>
          </div>
        )}
      </div>

      <CartSidebar
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        categoryName={categoryName}
        onQtyChange={handleQuantityChange}
        onPriceChange={handlePriceEdit}
        onRemove={handleRemoveFromCart}
        onClearAll={handleClearAllCart}
        onContinue={handleContinue}
      />

      {selectedServiceDetails && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4 py-10">
          <div
            className="
        w-full max-w-lg
        bg-white rounded-2xl shadow-2xl
        flex flex-col overflow-hidden
        max-h-[calc(100vh-140px)]
      "
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b bg-white">
              <h3 className="text-lg font-bold text-[#005b9f]">
                Service Details
              </h3>
              <button
                onClick={closeDetailsModal}
                className="p-2 rounded-lg hover:bg-slate-100 transition"
                aria-label="Close details"
              >
                <X className="h-5 w-5 text-slate-700" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {selectedServiceDetails?.image && (
                <img
                  src={selectedServiceDetails.image}
                  alt={selectedServiceDetails.name}
                  className="w-full h-44 sm:h-52 object-cover rounded-xl mb-4"
                />
              )}

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-700">
                    Service Name
                  </p>
                  <p className="text-slate-900">
                    {selectedServiceDetails?.name}
                  </p>
                </div>

                {selectedServiceDetails?.description && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      Description
                    </p>
                    <p className="text-slate-900 leading-relaxed">
                      {selectedServiceDetails.description}
                    </p>
                  </div>
                )}

                {selectedServiceDetails?.basePrice != null && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      Base Price
                    </p>
                    <p className="text-slate-900">
                      Rs. {selectedServiceDetails.basePrice}
                    </p>
                  </div>
                )}

                {selectedServiceDetails?.duration && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      Duration
                    </p>
                    <p className="text-slate-900">
                      {selectedServiceDetails.duration}
                    </p>
                  </div>
                )}

                {selectedServiceDetails?.availability && (
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      Availability
                    </p>
                    <p className="text-slate-900">
                      {selectedServiceDetails.availability}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t bg-white px-5 py-4">
              <button
                onClick={closeDetailsModal}
                className="w-full bg-[#005b9f] text-white py-3 rounded-xl hover:bg-[#004a80] transition font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showServiceTypeModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6 animate-fadeIn">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#005b9f]">
                Choose Service Type
              </h2>
              <button
                onClick={() => setShowServiceTypeModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={() => handleServiceSelect("live")}
                  disabled={!cartItems.length}
                  className="w-full bg-[#005b9f] text-white py-3 rounded-lg font-semibold hover:bg-[#004a80] transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Zap className="h-5 w-5 text-white" />
                  Live Now
                </button>
                <p className="text-xs text-gray-600 text-center -mt-1">
                  Handyman will be searched immediately
                </p>
              </div>

              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={() => handleServiceSelect("schedule")}
                  disabled={!cartItems.length}
                  className="w-full bg-yellow-400 text-black py-3 rounded-lg font-semibold hover:bg-yellow-500 transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Clock className="h-5 w-5 text-black" />
                  Schedule
                </button>
                <p className="text-xs text-gray-600 text-center -mt-1">
                  Pick date & time for your service
                </p>
              </div>

              <button
                onClick={() => setShowServiceTypeModal(false)}
                className="w-full text-gray-500 text-sm mt-1 underline"
              >
                Cancel
              </button>
            </div>

            <div className="mt-4 text-xs text-slate-500">
              Total:{" "}
              <span className="font-bold text-[#005b9f]">
                Rs. {cartTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubCategories;
